import { useEffect, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, MessageCircle, Package, ArrowRight } from "lucide-react";
import { inr } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderSuccess() {
  const { orderId } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.order || null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (order) return;
    axios
      .get(`${API}/orders/${orderId}`)
      .then((r) =>
        setOrder({
          id: orderId,
          name: r.data.customer_name,
          total: r.data.total,
          items: r.data.items || [],
          status: r.data.status,
          payment: r.data.payment,
        })
      )
      .catch(() => setNotFound(true));
  }, [orderId, order]);

  const summary = (order?.items || []).map((i) => `${i.qty} x ${i.name}`).join(", ");
  const msg = encodeURIComponent(
    `New PAID order #${orderId} — ${order?.name || ""}\nItems: ${summary}\nTotal: Rs.${order?.total ?? ""}`
  );
  const waLink = `https://wa.me/917356572909?text=${msg}`;

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-6 py-16" data-testid="order-success-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-lg"
      >
        <div className="text-center">
          <motion.div
            initial={{ scale: 0, rotate: -30 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.2 }}
            className="mx-auto w-20 h-20 rounded-full bg-[#C5A059] flex items-center justify-center"
          >
            <Check size={36} strokeWidth={2} className="text-[#2D1B36]" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="mt-8 font-serif-d font-light text-4xl md:text-5xl text-[#2D1B36] tracking-tight"
          >
            Thank you, <span className="italic text-[#C5A059]">it's on its way.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55, duration: 0.7 }}
            className="mt-4 text-[#514357] leading-relaxed"
          >
            {notFound
              ? "We couldn't find that order — but don't worry, call us and we'll sort it out."
              : "Your payment was received. We'll call you shortly to confirm delivery."}
          </motion.p>
        </div>

        {order && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-10 bg-white border border-[#E8E4DF] p-8"
            data-testid="order-summary-card"
          >
            <div className="flex items-center justify-between pb-5 border-b border-[#E8E4DF]">
              <span className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#8A818F] flex items-center gap-2">
                <Package size={13} /> Order
              </span>
              <span data-testid="success-order-id" className="font-serif-d text-2xl text-[#2D1B36]">#{orderId}</span>
            </div>
            <ul className="py-5 space-y-2.5">
              {order.items.map((i, idx) => (
                <li key={idx} className="flex justify-between gap-4 text-sm text-[#514357]">
                  <span>{i.qty} × {i.name}</span>
                  <span className="text-[#2D1B36]">{inr(i.price * i.qty)}</span>
                </li>
              ))}
            </ul>
            <div className="pt-5 border-t border-[#E8E4DF] flex justify-between items-baseline">
              <span className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#8A818F]">
                {order.payment || "Paid online"}
              </span>
              <span data-testid="success-order-total" className="font-serif-d text-3xl text-[#2D1B36]">{inr(order.total)}</span>
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 space-y-3"
        >
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="success-whatsapp-button"
            className="w-full flex items-center justify-center gap-2 bg-[#2E6669] text-[#FBF9F6] font-accent text-xs tracking-[0.25em] uppercase py-4 hover:bg-[#1E4749] transition-colors duration-300"
          >
            <MessageCircle size={15} /> Send Order to Shop WhatsApp
          </a>
          <Link
            to="/account"
            data-testid="success-my-account-link"
            className="w-full flex items-center justify-center gap-2 border border-[#C5A059]/60 text-[#4A2B58] font-accent text-xs tracking-[0.25em] uppercase py-4 hover:bg-[#C5A059]/10 transition-colors duration-300"
          >
            Track in My Account <ArrowRight size={13} />
          </Link>
          <Link
            to="/"
            data-testid="success-continue-shopping"
            className="block text-center font-accent text-[11px] tracking-[0.2em] uppercase text-[#8A818F] hover:text-[#C5A059] transition-colors pt-2"
          >
            Continue shopping
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
}
